import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'
import './BalanceSummary.css'

import { usePathname } from 'next/navigation'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { type Swiper as SwiperType } from 'swiper'
import { FreeMode, Mousewheel, Pagination } from 'swiper/modules'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'

import {
  FinanceBalance,
  FinanceBalanceState,
  FinanceBalanceTicketType
} from '@/api/models/finance'
import { ScreenSize } from '@/app/constants/index'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import { drawerDuration } from '@/components/shared/drawer/ui/Drawer'
import { CardSkeleton } from '@/components/shared/skeleton'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { BalanceSummaryVariant } from '../model/tickets.types'
import InvoiceListDrawer from './InvoiceListDrawer/InvoiceListDrawer'
import BalanceTicket from './tickets/BalanceTicket'

type Props = {
  data?: FinanceBalance | null
  isFetching: boolean
  refetch: () => void
  variant: BalanceSummaryVariant
  slidesOffsetAfter?: number
  useCustomTitles?: boolean
  isError: boolean
  error?: Error | null
}

const BalanceSummary: FC<Props> = ({
  data,
  isFetching,
  refetch,
  variant,
  slidesOffsetAfter,
  useCustomTitles = false,
  isError,
  error
}) => {
  const [balance, setBalance] = useState<FinanceBalanceTicketType>()
  const [invoiceListDrawerOpened, setInvoiceListDrawerOpened] = useState(false)
  const { reachGoal } = useYandexMetrika()
  const pathname = usePathname()

  const swiperRef = useRef<SwiperType | null>(null)

  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)

  const isSwiperVisible = isMobileScreen && (data?.tickets.length ?? 0) > 1

  const TicketWrapper = isSwiperVisible ? SwiperSlide : 'div'
  const ticketWrapperClass = isSwiperVisible ? '!w-[496px]' : ''

  const handleInvoiceListDrawerOpened = useCallback(
    (ticket?: FinanceBalanceTicketType) => {
      if (ticket) {
        setTimeout(() => setBalance(ticket), drawerDuration)
      }
      setInvoiceListDrawerOpened(prev => !prev)
    },
    []
  )

  const checkCurrentEvent = useCallback(
    (ticketIndex: number) => {
      if (pathname === '/') {
        return ticketIndex === 0
          ? YandexMetrikaEvent.MAIN_TOTAL_COMMUNAL
          : YandexMetrikaEvent.MAIN_TOTAL_CAPITAL
      }
      return ticketIndex === 0
        ? YandexMetrikaEvent.INVOICES_TOTAL_COMMUNAL
        : YandexMetrikaEvent.INVOICES_TOTAL_CAPITAL
    },
    [pathname]
  )

  const handleTicketButtonClick = useCallback(
    (ticket: FinanceBalanceTicketType, index: number) => {
      if (ticket.state === FinanceBalanceState.NotPaid && ticket.canPay) {
        reachGoal(checkCurrentEvent(index))
        handleInvoiceListDrawerOpened(ticket)
      } else {
        refetch()
      }
    },
    [checkCurrentEvent, handleInvoiceListDrawerOpened, reachGoal, refetch]
  )

  const balanceSummaryList = useMemo(
    () =>
      data?.tickets?.map((ticket, index) => (
        <TicketWrapper
          key={ticket.type}
          className={cn(ticketWrapperClass, 'sm:!h-[184px]')}
        >
          <BalanceTicket
            ticket={ticket}
            variant={variant}
            onButtonClick={() => handleTicketButtonClick(ticket, index)}
            useCustomTitles={useCustomTitles}
          />
        </TicketWrapper>
      )) || [],
    [
      TicketWrapper,
      data?.tickets,
      handleTicketButtonClick,
      ticketWrapperClass,
      useCustomTitles,
      variant
    ]
  )

  const billsPayDrawer = useMemo(
    () =>
      createPortal(
        balance && (
          <InvoiceListDrawer
            balance={balance}
            opened={invoiceListDrawerOpened}
            onClose={handleInvoiceListDrawerOpened}
            setInvoiceListDrawerOpened={setInvoiceListDrawerOpened}
          />
        ),
        document.body
      ),
    [handleInvoiceListDrawerOpened, invoiceListDrawerOpened, balance]
  )

  if (isFetching) {
    return <CardSkeleton />
  }

  if (error && isForbiddenError(error)) {
    return (
      <div className="rounded-16 bg-white p-16 lg:p-16">
        <ErrorForbidden className="mb-[20px] mt-[20px] sm:mb-[16px] sm:mt-[16px]" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-16 bg-white p-20 lg:p-16 sm:p-10">
        <ErrorCard />
      </div>
    )
  }

  if (!isSwiperVisible) {
    return (
      <>
        {balanceSummaryList}
        {billsPayDrawer}
      </>
    )
  }

  return (
    <>
      <SwiperComponent
        modules={[FreeMode, Mousewheel, Pagination]}
        slidesPerView="auto"
        spaceBetween={20}
        slidesOffsetAfter={slidesOffsetAfter}
        mousewheel
        watchOverflow
        threshold={10}
        pagination={{ clickable: true }}
        className="h-[208px] w-full"
        onInit={(swiper: SwiperType) => (swiperRef.current = swiper)}
      >
        {balanceSummaryList}
      </SwiperComponent>
      {billsPayDrawer}
    </>
  )
}

export default BalanceSummary
